import React, { useEffect, useState } from 'react';
import { Button, Switch } from 'antd';
import Icon, { CheckSquareOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  buildFilterSelector,
  compareDrawerTitleSelector,
  compareFiltersSelector,
  compareTypeSelector,
} from '@store/trends/base/selectors';
import { FilterBarFooter, StyledDrawer } from './styled';
import FilterBar from './FilterBar';
import {
  compareFilters,
  compareFiltersOnTrends,
  removeCompareRule,
  resetCompare,
  updateCompareRule,
} from '@store/trends/base/actions';
import CompareBar from './CompareBar';
import FilterBarAdvanced from './FilterBarAdvanced';
import { useNavigate } from 'react-router-dom';
import { tid } from '@utils/test';
import PrimaryFilterTag from './PrimaryFilterTag';
import { COMPARE_TYPES } from '@store/trends/base/reducer';
import CompareIco from '@icons/charts/compare.svg';
import { scrollToCompareFiltersBar } from '@components/summary-panel/CompareFiltersBar/utils';
import apptracker from '@utils/sensor/apptracker';
import { APPLY_COMPARE } from '@src/utils/sensor/track';
import { useNewFilterDialogEnabled } from '@src/utils/filter';
import { AdvancedFilterBuilder } from '@src/components/filter/containers/AdvancedFilterBuilder';
import { modeAtom, TABLE_MODES } from '../atoms';
import { useAtom } from 'jotai';

const HelpText = () => (
  <div>
    <ol>
      <li>Add saved filters using drop-down.</li>
      <li>
        Add top 10 dimension values as filters using <Icon component={CompareIco} /> icon
      </li>
      <li>
        Add specific filters using <PlusOutlined /> icon
      </li>
      <li>
        Build complex AND/OR conditions from multiple dimensions using <CheckSquareOutlined /> icon
      </li>
    </ol>
  </div>
);

function CompareBuilder() {
  const dispatch = useDispatch();
  const filters = useSelector(compareFiltersSelector);
  const [advanced, setAdvanced] = useState(true);
  const navigate = useNavigate();
  const title = useSelector(compareDrawerTitleSelector);

  const generatedFilter = useSelector(buildFilterSelector);
  const { rule = [] } = generatedFilter;
  const type = useSelector(compareTypeSelector);

  const [applying, setApplying] = useState(false);

  const newFilterDialogEnabled = useNewFilterDialogEnabled();

  const [mode, setMode] = useAtom(modeAtom);
  const visible = mode === TABLE_MODES.compare;
  const onToggle = (value) => {
    const newMode = value ? TABLE_MODES.compare : TABLE_MODES.drilldown;
    setMode(newMode);
  };

  useEffect(() => {
    if (filters.length > 0) {
      onToggle(true);
    }
  }, [filters.length]);

  const handleOk = () => {
    setApplying(true);
    // to render button state change
    setTimeout(() => {
      if (type === COMPARE_TYPES.trends) {
        dispatch(compareFiltersOnTrends());
      } else {
        dispatch(compareFilters({ navigate }));
      }
      handleCancel();
      scrollToCompareFiltersBar();
      apptracker(APPLY_COMPARE, { type });
    }, 50);
  };

  const handleCancel = () => {
    onToggle(false);
    // to accomodate drawer close animation
    setTimeout(() => {
      dispatch(resetCompare());
      setApplying(false);
    }, 50);
  };

  const onSwitchChange = (checked: boolean) => {
    setAdvanced(checked);
  };

  const getFilterBarComponent = () => {
    if (rule.length === 0) return <HelpText />;

    if (newFilterDialogEnabled) {
      return (
        <AdvancedFilterBuilder
          value={rule as any[]}
          onRuleRemove={removeCompareRule}
          onRuleUpdate={updateCompareRule}
        />
      );
    }

    return advanced ? (
      <FilterBarAdvanced
        rules={rule}
        removeRule={removeCompareRule}
        updateRule={updateCompareRule}
      />
    ) : (
      <FilterBar rules={rule} removeRule={removeCompareRule} />
    );
  };

  return (
    <>
      <StyledDrawer
        title={
          <div style={{ alignItems: 'center', gap: 8, display: 'flex' }}>
            <span>{title}</span>
            {!newFilterDialogEnabled && (
              <Switch
                checkedChildren="Advanced"
                unCheckedChildren="Basic"
                size="small"
                onChange={onSwitchChange}
                defaultChecked
                {...tid('compare-drawer-switch')}
              />
            )}
          </div>
        }
        placement="top"
        onClose={handleCancel}
        open={visible}
        key="compare-builder"
        mask={false}
        height={250}
        zIndex={999}
        footer={
          <FilterBarFooter {...tid('compare-drawer-footer')}>
            <Button type="primary" onClick={handleOk} disabled={applying || filters.length <= 0}>
              {applying ? 'Applying...' : 'Apply'}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </FilterBarFooter>
        }
      >
        <div {...tid('compare-drawer-body')}>
          {type === COMPARE_TYPES.trends && <PrimaryFilterTag />}
          <CompareBar filters={filters} type={type} />
          <div>{getFilterBarComponent()}</div>
        </div>
      </StyledDrawer>
    </>
  );
}

export default CompareBuilder;
